import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import CampaignsAPI from '../../api/campaigns';
import CampaignSuppressionsAPI from '../../api/campaignSuppressions';
import AnalyticsHelper from '../../helper/AnalyticsHelper';
import { CAMPAIGNS_EVENTS } from '../../helper/AnalyticsHelper/events';
import { CAMPAIGN_TYPES } from 'shared/constants/campaign';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

export const state = {
  records: [],
  accountId: null,
  uiFlags: {
    isFetching: false,
    fetchError: false,
    isCreating: false,
  },
};

let campaignsFetchSequence = 0;

export const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getCampaigns:
    _state =>
    (campaignType, inboxChannelTypes = null) => {
      let filteredRecords = _state.records.filter(
        record => record.campaign_type === campaignType
      );

      if (inboxChannelTypes && Array.isArray(inboxChannelTypes)) {
        filteredRecords = filteredRecords.filter(record => {
          return (
            record.inbox &&
            inboxChannelTypes.includes(record.inbox.channel_type)
          );
        });
      }

      return filteredRecords.sort((a1, a2) => a1.id - a2.id);
    },
  getSMSCampaigns: (_state, _getters) => {
    const smsChannelTypes = [INBOX_TYPES.SMS, INBOX_TYPES.TWILIO];
    return _getters.getCampaigns(CAMPAIGN_TYPES.ONE_OFF, smsChannelTypes);
  },
  getWhatsAppCampaigns: (_state, _getters) => {
    // Inclui WAHA (Channel::Api) — campanha drip não-oficial aparece na mesma página
    // (que tem os controles iniciar/pausar/prévia). Simétrico a getWhatsappCampaignInboxes.
    const whatsappChannelTypes = [INBOX_TYPES.WHATSAPP, INBOX_TYPES.API];
    return _getters.getCampaigns(CAMPAIGN_TYPES.ONE_OFF, whatsappChannelTypes);
  },
  getLiveChatCampaigns: (_state, _getters) => {
    const liveChatChannelTypes = [INBOX_TYPES.WEB];
    return _getters.getCampaigns(CAMPAIGN_TYPES.ONGOING, liveChatChannelTypes);
  },
  getAllCampaigns: _state => {
    return _state.records;
  },
};

export const actions = {
  get: async function getCampaigns({
    commit,
    rootGetters,
    state: currentState,
  }) {
    const accountId = rootGetters?.getCurrentAccountId ?? null;
    campaignsFetchSequence += 1;
    const request = campaignsFetchSequence;
    commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: true });
    if (currentState.accountId !== accountId) {
      commit(types.SET_CAMPAIGNS, []);
      commit(types.SET_CAMPAIGN_ACCOUNT, accountId);
      commit(types.SET_CAMPAIGN_UI_FLAG, {
        fetchError: false,
        isCreating: false,
        isUpdating: false,
        isDeleting: false,
      });
    }
    try {
      const response = await CampaignsAPI.get();
      if (
        request !== campaignsFetchSequence ||
        accountId !== (rootGetters?.getCurrentAccountId ?? null)
      ) {
        return;
      }
      commit(types.SET_CAMPAIGNS, response.data);
      commit(types.SET_CAMPAIGN_UI_FLAG, { fetchError: false });
    } catch (error) {
      if (
        request === campaignsFetchSequence &&
        accountId === (rootGetters?.getCurrentAccountId ?? null)
      ) {
        commit(types.SET_CAMPAIGNS, []);
        commit(types.SET_CAMPAIGN_UI_FLAG, { fetchError: true });
      }
    } finally {
      if (
        request === campaignsFetchSequence &&
        accountId === (rootGetters?.getCurrentAccountId ?? null)
      ) {
        commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: false });
      }
    }
  },
  create: async function createCampaign({ commit, rootGetters }, campaignObj) {
    const accountId = rootGetters?.getCurrentAccountId ?? null;
    const isCurrentAccount = () =>
      accountId === (rootGetters?.getCurrentAccountId ?? null);
    commit(types.SET_CAMPAIGN_UI_FLAG, { isCreating: true });
    try {
      const response = await CampaignsAPI.create(campaignObj);
      if (isCurrentAccount()) commit(types.ADD_CAMPAIGN, response.data);
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      if (isCurrentAccount()) {
        commit(types.SET_CAMPAIGN_UI_FLAG, { isCreating: false });
      }
    }
  },
  update: async ({ commit, rootGetters }, { id, ...updateObj }) => {
    const accountId = rootGetters?.getCurrentAccountId ?? null;
    const isCurrentAccount = () =>
      accountId === (rootGetters?.getCurrentAccountId ?? null);
    commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: true });
    try {
      const response = await CampaignsAPI.update(id, updateObj);
      if (isCurrentAccount()) {
        AnalyticsHelper.track(CAMPAIGNS_EVENTS.UPDATE_CAMPAIGN);
        commit(types.EDIT_CAMPAIGN, response.data);
      }
    } catch (error) {
      throw new Error(error);
    } finally {
      if (isCurrentAccount()) {
        commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: false });
      }
    }
  },
  delete: async ({ commit, rootGetters }, id) => {
    const accountId = rootGetters?.getCurrentAccountId ?? null;
    const isCurrentAccount = () =>
      accountId === (rootGetters?.getCurrentAccountId ?? null);
    commit(types.SET_CAMPAIGN_UI_FLAG, { isDeleting: true });
    try {
      await CampaignsAPI.delete(id);
      if (isCurrentAccount()) {
        AnalyticsHelper.track(CAMPAIGNS_EVENTS.DELETE_CAMPAIGN);
        commit(types.DELETE_CAMPAIGN, id);
      }
    } catch (error) {
      throw new Error(error);
    } finally {
      if (isCurrentAccount()) {
        commit(types.SET_CAMPAIGN_UI_FLAG, { isDeleting: false });
      }
    }
  },

  // Disparo em massa v2 (drip): inicia/pausa/retoma e refaz a busca pra refletir
  // o drip_status na lista.
  startDrip: async ({ dispatch }, id) => {
    await CampaignsAPI.start(id);
    await dispatch('get');
  },
  pauseDrip: async ({ dispatch }, id) => {
    await CampaignsAPI.pause(id);
    await dispatch('get');
  },
  resumeDrip: async ({ dispatch }, id) => {
    await CampaignsAPI.resume(id);
    await dispatch('get');
  },
  cancelDrip: async ({ dispatch }, id) => {
    await CampaignsAPI.cancel(id);
    await dispatch('get');
  },
  previewDrip: async (_store, id) => {
    const response = await CampaignsAPI.preview(id);
    return response.data;
  },
  fetchRecipients: async (_store, id) => {
    const response = await CampaignsAPI.recipients(id);
    return response.data;
  },
  // Lista de supressão (LGPD): insere telefones em lote (blacklist por conta).
  suppressPhones: async (_store, phoneNumbers) => {
    const response = await CampaignSuppressionsAPI.create({
      phone_numbers: phoneNumbers,
    });
    return response.data;
  },
};

export const mutations = {
  [types.SET_CAMPAIGN_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.SET_CAMPAIGN_ACCOUNT](_state, accountId) {
    _state.accountId = accountId;
  },

  [types.ADD_CAMPAIGN]: MutationHelpers.create,
  [types.SET_CAMPAIGNS]: MutationHelpers.set,
  [types.EDIT_CAMPAIGN]: MutationHelpers.update,
  [types.DELETE_CAMPAIGN]: MutationHelpers.destroy,
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
